import type { CurrentConditions as CurrentConditionsData } from "../api/types";
import { formatTemperature, formatWind } from "../lib/format";

export interface CurrentConditionsProps {
  data: CurrentConditionsData;
}

function CloudAccentIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className="text-surface"
    >
      <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
    </svg>
  );
}

function MetricChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-1 flex-col gap-1 rounded-[18px] bg-surface p-3">
      <p className="text-[12px] text-on-surface-variant">{label}</p>
      <p className="text-[14px] font-medium tabular text-on-surface">{value}</p>
    </div>
  );
}

export default function CurrentConditions({ data }: CurrentConditionsProps) {
  const tempLabel = formatTemperature(data.temperature);
  const feelsValue =
    data.feelsLike !== null
      ? formatTemperature(data.feelsLike)
      : formatTemperature(data.temperature);
  const precipValue =
    data.precipitationChancePct !== null ? `${data.precipitationChancePct}%` : "—";
  const windValue = formatWind(data.wind.speed, data.wind.direction);

  return (
    <section
      aria-label="Current conditions"
      className="flex flex-col gap-4 rounded-2xl bg-surface-container-high p-6 shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p
            className="text-[64px] font-bold tabular leading-none text-on-surface"
            aria-label={`Current temperature: ${tempLabel}`}
          >
            {tempLabel}
          </p>
          <p className="mt-1 text-[18px] font-medium text-on-surface-variant">
            {data.shortForecast}
          </p>
        </div>
        <div
          className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-tertiary"
          aria-hidden
        >
          <CloudAccentIcon />
        </div>
      </div>

      <div className="flex gap-2" aria-label="Current conditions detail">
        <MetricChip label="Precip" value={precipValue} />
        <MetricChip label="Wind" value={windValue} />
        <MetricChip label="Feels Like" value={feelsValue} />
      </div>
    </section>
  );
}
